import React, {useRef, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FileUpload} from "primereact/fileupload";
import Icon from "../../system/components/icon";
import StripedProgressBar from "../../system/components/striped-progress-bar";
import {useTranslate} from "../../system/useTranslate";
import {convertWordFileToMarkdown, isDocxFile, WORD_FILE_LIMITS} from "../../system/word-post";

const INITIAL_STATE = {
    stage: "idle",
    progress: 0,
    imageCount: 0,
    error: null,
    markdown: "",
    fileName: "",
};

function WordImportDialog({visible, setVisible, onComplete, onCancel, uploader}) {
    const translate = useTranslate();
    const abortRef = useRef(null);
    const fileRef = useRef(null);
    const [state, setState] = useState(INITIAL_STATE);

    const close = (notify = true) => {
        abortRef.current?.abort();
        abortRef.current = null;
        fileRef.current = null;
        setState(INITIAL_STATE);
        setVisible?.(false);
        if (notify) onCancel?.();
    };

    const runConversion = async (file) => {
        abortRef.current = new AbortController();
        setState((current) => ({...current, stage: "converting", fileName: file.name}));
        try {
            const result = await convertWordFileToMarkdown(file, {
                signal: abortRef.current.signal,
                uploader,
                onProgress: (done, total) => {
                    const percent = total > 0 ? Math.round((done / total) * 100) : 100;
                    setState((current) => ({...current, stage: "uploading", progress: percent}));
                },
            });

            if (abortRef.current?.signal.aborted) {
                setState(INITIAL_STATE);
                return;
            }
            if (!result.ok) {
                setState((current) => ({...current, stage: "error", error: result.error}));
                return;
            }
            setState((current) => ({
                ...current,
                stage: "done",
                markdown: result.markdown,
                imageCount: result.images.length,
                progress: 100,
            }));
        } catch (error) {
            if (error?.name === "AbortError" || abortRef.current?.signal.aborted) {
                setState(INITIAL_STATE);
            } else {
                setState((current) => ({...current, stage: "error", error: "word-import-failed"}));
            }
        }
    };

    const handleSelection = async (event) => {
        const file = event.files?.[0];
        if (!file) return;
        if (!isDocxFile(file)) {
            setState((current) => ({...current, stage: "error", error: "word-file-type-not-allowed"}));
            return;
        }
        if (file.size > WORD_FILE_LIMITS.maxFileSize) {
            setState((current) => ({...current, stage: "error", error: "word-file-too-large"}));
            return;
        }
        fileRef.current = file;
        runConversion(file);
    };

    const importMarkdown = () => {
        const markdown = state.markdown;
        close(false);
        if (markdown) onComplete?.(markdown);
    };

    const chooseOptions = {
        label: translate("choose-word-file"),
        className: "p-button-text border-none shadow-none text-xs flex gap-1 p-2",
        icon: <Icon icon="mi mi:upload_file" className="text-lg"/>,
    };
    const cancelOptions = {
        label: translate("cancel"),
        className: "p-button-text border-none shadow-none text-xs flex gap-1 p-2",
        icon: <Icon icon="mi mi:cancel" className="text-lg"/>,
    };

    const processing = state.stage === "converting" || state.stage === "uploading";

    return (
        <Dialog
            className="w-5 rtl"
            header={<span className="text-lg">{translate("word-import-title")}</span>}
            visible={visible}
            onHide={() => close()}
            footer={(
                <div className="flex justify-content-end gap-2">
                    {processing && (
                        <Button label={translate("cancel")} icon="pi pi-times" className="p-button-text text-xs gap-2"
                                onClick={() => abortRef.current?.abort()}/>
                    )}
                    {state.stage === "done" && (
                        <>
                            <Button label={translate("cancel")} className="p-button-text text-xs" onClick={() => close()}/>
                            <Button label={translate("word-import-insert")} icon="pi pi-check" className="text-xs gap-2"
                                    onClick={importMarkdown}/>
                        </>
                    )}
                </div>
            )}
        >
            {state.stage === "done" ? (
                <DoneContent state={state} translate={translate}/>
            ) : processing ? (
                <ProcessingContent state={state} translate={translate}/>
            ) : state.stage === "error" ? (
                <ErrorContent state={state} translate={translate} onReset={() => setState(INITIAL_STATE)}/>
            ) : (
                <FileUpload
                    accept=".docx"
                    customUpload
                    auto
                    mode="basic"
                    uploadHandler={handleSelection}
                    chooseOptions={chooseOptions}
                    cancelOptions={cancelOptions}
                />
            )}
        </Dialog>
    );
}

function ProcessingContent({state, translate}) {
    return (
        <div className="flex flex-column align-items-center gap-3 p-3 rtl">
            <Icon icon="mi mi:file_download" className="text-5xl text-primary"/>
            <span className="text-sm text-700">
                {translate(state.stage === "uploading" ? "word-uploading-images" : "word-converting")}
            </span>
            {state.fileName && (
                <span className="text-xs text-400 max-w-full overflow-hidden text-overflow-ellipsis white-space-nowrap">
                    {state.fileName}
                </span>
            )}
            {state.stage === "uploading" && (
                <StripedProgressBar value={state.progress} ariaLabel={translate("word-uploading-images")}/>
            )}
        </div>
    );
}

function DoneContent({state, translate}) {
    const preview = state.markdown.length > 2500 ? `${state.markdown.slice(0, 2500)} ...` : state.markdown;
    return (
        <div className="flex flex-column gap-2 rtl">
            <div className="flex align-items-center gap-2 text-sm font-bold text-green-600">
                <Icon icon="mi mi:check_circle" className="text-xl"/>
                <span>{translate("word-import-success")}</span>
            </div>
            {state.imageCount > 0 && (
                <span className="text-xs text-400">{translate("word-images-uploaded")} : {state.imageCount}</span>
            )}
            <div className="surface-100 border-round p-3 max-h-16rem overflow-auto">
                <pre className="text-xs white-space-pre-wrap text-700 m-0 ltr">{preview}</pre>
            </div>
        </div>
    );
}

function ErrorContent({state, translate, onReset}) {
    return (
        <div className="flex flex-column gap-2 p-2 rtl">
            <div className="flex align-items-center gap-2 text-red-600">
                <Icon icon="mi mi:error" className="text-xl"/>
                <span className="text-sm">{translate(state.error || "word-import-failed")}</span>
            </div>
            <div className="flex justify-content-end gap-2 mt-3">
                <Button label={translate("close")} className="p-button-text text-xs" onClick={onReset}/>
            </div>
        </div>
    );
}

export default WordImportDialog;